import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

function hexToRgb(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  if (!m) return { r: 200, g: 90, b: 50 };
  return { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) };
}
function rgbToHex(r, g, b) {
  return "#" + [r, g, b].map((x) => clamp(Math.round(x), 0, 255).toString(16).padStart(2, "0")).join("");
}
function rgbToHsv(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
  let h = 0;
  if (d) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60; if (h < 0) h += 360;
  }
  return { h, s: max === 0 ? 0 : d / max, v: max };
}
function hsvToRgb(h, s, v) {
  const c = v * s, x = c * (1 - Math.abs(((h / 60) % 2) - 1)), m = v - c;
  let r = 0, g = 0, b = 0;
  if (h < 60) { r = c; g = x; } else if (h < 120) { r = x; g = c; }
  else if (h < 180) { g = c; b = x; } else if (h < 240) { g = x; b = c; }
  else if (h < 300) { r = x; b = c; } else { r = c; b = x; }
  return { r: (r + m) * 255, g: (g + m) * 255, b: (b + m) * 255 };
}
function hsvToHex(h, s, v) { const { r, g, b } = hsvToRgb(h, s, v); return rgbToHex(r, g, b); }

// Inline, on-brand color picker. Updates live via onChange; onCommit fires on release / hex confirm.
export const ColorPicker = ({ value, onChange, onCommit }) => {
  const [hsv, setHsv] = useState(() => { const { r, g, b } = hexToRgb(value); return rgbToHsv(r, g, b); });
  const [hex, setHex] = useState(value || "#C85A32");
  const svRef = useRef(null);
  const hueRef = useRef(null);
  const hsvRef = useRef(hsv);
  const hexRef = useRef(hex);
  const cbRef = useRef({ onChange, onCommit });
  cbRef.current = { onChange, onCommit };

  // Sync when the selected part (external value) changes.
  useEffect(() => {
    const { r, g, b } = hexToRgb(value);
    const c = rgbToHsv(r, g, b);
    hsvRef.current = c; setHsv(c);
    hexRef.current = value || "#C85A32"; setHex(value || "#C85A32");
  }, [value]);

  const apply = (nh, ns, nv, commit) => {
    const c = { h: nh, s: ns, v: nv };
    hsvRef.current = c; setHsv(c);
    const hx = hsvToHex(nh, ns, nv);
    hexRef.current = hx; setHex(hx);
    cbRef.current.onChange && cbRef.current.onChange(hx);
    if (commit) cbRef.current.onCommit && cbRef.current.onCommit(hx);
  };

  const svFromEvent = (e) => {
    const r = svRef.current.getBoundingClientRect();
    const ns = clamp((e.clientX - r.left) / r.width, 0, 1);
    const nv = clamp(1 - (e.clientY - r.top) / r.height, 0, 1);
    apply(hsvRef.current.h, ns, nv, false);
  };
  const hueFromEvent = (e) => {
    const r = hueRef.current.getBoundingClientRect();
    const nh = clamp((e.clientX - r.left) / r.width, 0, 1) * 360;
    apply(nh, hsvRef.current.s, hsvRef.current.v, false);
  };

  const startDrag = (mover, e) => {
    e.preventDefault();
    mover(e);
    const move = (ev) => mover(ev);
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const onHexChange = (val) => {
    setHex(val);
    if (/^#?[a-f\d]{6}$/i.test(val)) {
      const norm = val.startsWith("#") ? val : "#" + val;
      const { r, g, b } = hexToRgb(norm);
      const c = rgbToHsv(r, g, b);
      hsvRef.current = c; setHsv(c);
      hexRef.current = norm;
      cbRef.current.onChange && cbRef.current.onChange(norm);
    }
  };
  const selectColor = () => {
    if (/^#?[a-f\d]{6}$/i.test(hex)) cbRef.current.onCommit && cbRef.current.onCommit(hex.startsWith("#") ? hex : "#" + hex);
  };

  const hueColor = hsvToHex(hsv.h, 1, 1);

  return (
    <div className="select-none" data-testid="studio-color-picker">
      <div
        ref={svRef}
        onPointerDown={(e) => startDrag(svFromEvent, e)}
        className="relative w-full h-28 rounded-md cursor-crosshair touch-none"
        style={{
          background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, transparent), ${hueColor}`,
        }}
        data-testid="studio-color-sv-area"
      >
        <div
          className="absolute w-3.5 h-3.5 rounded-full border-2 border-white shadow -translate-x-1/2 -translate-y-1/2 pointer-events-none"
          style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%`, background: hex }}
        />
      </div>

      <div
        ref={hueRef}
        onPointerDown={(e) => startDrag(hueFromEvent, e)}
        className="relative w-full h-3.5 rounded-full mt-3 cursor-pointer touch-none"
        style={{ background: "linear-gradient(to right,#f00 0%,#ff0 17%,#0f0 33%,#0ff 50%,#00f 67%,#f0f 83%,#f00 100%)" }}
        data-testid="studio-color-hue-slider"
      >
        <div
          className="absolute top-1/2 w-4 h-4 rounded-full border-2 border-white shadow -translate-x-1/2 -translate-y-1/2 pointer-events-none"
          style={{ left: `${(hsv.h / 360) * 100}%`, background: hueColor }}
        />
      </div>

      <div className="flex items-center gap-2 mt-3">
        <div className="w-8 h-8 rounded-md border border-[#C8C2AC] shrink-0" style={{ background: hex }} />
        <div className="flex items-center flex-1 bg-white border border-[#C8C2AC] rounded-md px-2 h-8">
          <span className="text-[#8C867B] text-sm">#</span>
          <input
            value={hex.replace(/^#/, "")}
            onChange={(e) => onHexChange(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && selectColor()}
            maxLength={6}
            data-testid="studio-color-hex-input"
            className="flex-1 min-w-0 bg-transparent outline-none text-sm font-mono uppercase text-[#1A1918]"
          />
        </div>
      </div>

      <Button
        onClick={selectColor}
        className="w-full mt-3 h-9 rounded-full bg-[#C85A32] hover:bg-[#B04B26] text-[#F8F6F0]"
        data-testid="studio-color-select-button"
      >
        <Check className="h-4 w-4 mr-2" /> Select color
      </Button>
    </div>
  );
};

export default ColorPicker;
